import { MessageInlineImage } from 'lib-shared-legacy-types';

import { AttachmentFile } from 'app/utils/attachments';

export interface TextNode {
  node: Text;
  startOffset: number;
  endOffset: number;
}

export interface RedactedTextNode extends TextNode {
  originalText: string;
}

export interface File extends AttachmentFile {
  markedForDeletion?: boolean;
}

export const redactedCharacter = '█';

export const toggleFileDeletion = (currentFiles: File[], url: string) => {
  return currentFiles.map((file) => {
    if (file.relative_uri !== url) {
      return file;
    }
    return {
      ...file,
      markedForDeletion: file.markedForDeletion
        ? !file.markedForDeletion
        : true,
    };
  });
};

export const getTextNodesFromRange = (range: Range): TextNode[] => {
  const commonAncestorContainer = range.commonAncestorContainer;

  if (commonAncestorContainer.nodeType === Node.TEXT_NODE) {
    return [
      {
        node: range.startContainer as Text,
        startOffset: range.startOffset,
        endOffset: range.endOffset,
      },
    ];
  }

  const textNodes: TextNode[] = [];

  // create a tree walker that visits only text nodes in the range
  const walker = document.createTreeWalker(
    commonAncestorContainer,
    NodeFilter.SHOW_TEXT,
    null,
  );

  let currentNode: Node | null;

  while ((currentNode = walker.nextNode())) {
    if (range.intersectsNode(currentNode)) {
      const startOffset =
        currentNode === range.startContainer ? range.startOffset : 0;
      const endOffset =
        currentNode === range.endContainer
          ? range.endOffset
          : (currentNode as Text).length;
      textNodes.push({
        node: currentNode as Text,
        startOffset,
        endOffset,
      });
    }
  }

  return textNodes;
};

export const replaceSelectedText = (
  contentDiv: HTMLDivElement | null,
): RedactedTextNode[] => {
  const selection = globalThis.getSelection();

  if (!selection || selection.rangeCount === 0) {
    return [];
  }

  const range = selection.getRangeAt(0);

  // Check if selection is within the content div
  if (!contentDiv?.contains(range.commonAncestorContainer)) {
    return [];
  }

  const selectedText = selection.toString().replaceAll(redactedCharacter, '');
  if (selectedText.length === 0) {
    return [];
  }

  const redactedTextNodes = redactTextNodes(getTextNodesFromRange(range));

  // Clear selection
  selection.removeAllRanges();
  return redactedTextNodes;
};

export const redactTextNodes = (textNodes: TextNode[]): RedactedTextNode[] => {
  const redactedTextNodes: RedactedTextNode[] = [];

  for (const { node, startOffset, endOffset } of textNodes) {
    const originalText = node.textContent || '';
    const selectedPart = originalText.substring(startOffset, endOffset);

    redactedTextNodes.push({
      node,
      startOffset,
      endOffset,
      originalText: selectedPart,
    });

    // replace all non-spacing characters with the redacted character
    const replacement = selectedPart.replaceAll(/\S/g, redactedCharacter);

    node.textContent =
      originalText.substring(0, startOffset) +
      replacement +
      originalText.substring(endOffset);
  }

  return redactedTextNodes;
};

export const restoreRedactedTextNodes = (textNodes: RedactedTextNode[]) => {
  for (const { node, startOffset, endOffset, originalText } of textNodes) {
    const text = node.textContent || '';

    node.textContent =
      text.substring(0, startOffset) + originalText + text.substring(endOffset);
  }
};

export const prepareFilesForUpload = (files: File[]): MessageInlineImage[] => {
  return files
    .filter((file) => !file.markedForDeletion)
    .map((file) => ({
      url: file.relative_uri,
      pretty_name: file.name,
    }));
};
